import {
  Component,
  inject,
  input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

import { OrpcService } from '../../services/orpc-client.service';
import type { contractOutput as backendOutput } from '#backend/contract';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-file-params-form',
  imports: [
    MatSelectModule,
    MatFormFieldModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
  ],
  standalone: true,
  templateUrl: './file-params-form.component.html',
  styleUrl: './file-params-form.component.scss',
})
export class FileParamsFormComponent implements OnChanges, OnInit {
  ngOnInit(): void {
    this.form.controls.featurePrimaryKey.valueChanges.subscribe(() =>
      this.checkPk(),
    );
  }
  private readonly orpc = inject(OrpcService);
  fileName = input<string>();
  originalFileName = input<string>(); // Nome original do arquivo antes do upload
  module = input<'compliance' | 'history' | 'monitoring'>('compliance');
  isCarRequired = input<boolean>(false);
  showCarField = input<boolean>(false);
  isCarFieldHidden: boolean = false;

  /**
   * Retorna true quando o tipo de arquivo selecionado é Planilha Excel
   */
  metadata:
    | backendOutput['storage']['getShpMetadata']
    | backendOutput['storage']['getKMLMetadata']
    | backendOutput['storage']['getExcelMetadata']
    | null = null;
  form = new FormGroup({
    featurePrimaryKey: new FormControl('', {
      validators: [Validators.required],
    }),
    contract: new FormControl(''),
    insurancePolicy: new FormControl(''),
    farmName: new FormControl(''),
    identityDocument: new FormControl(''),
    producer: new FormControl(''),
    car: new FormControl(''),
  });

  /**
   * Verifica se o arquivo é do tipo Excel
   */
  private isExcelFile(fileName: string): boolean {
    return fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Verifica se o fileName realmente mudou
    if (changes['fileName']) {
      const currentFileName = this.fileName();
      const previousFileName = changes['fileName'].previousValue;

      if (currentFileName !== previousFileName) {
        // Só reseta quando o fileName muda, não em toda alteração
        if (!currentFileName) {
          this.resetForm();
        } else {
          this.getMetadata();
          this.checkPk();
        }
      }
    }

    if (changes['isCarRequired']) {
      this.updateCarValidator(this.isCarRequired());
    }
    if (changes['showCarField']) {
      this.updateCarFieldVisibility(this.showCarField());
    }
  }

  // Atualiza dinamicamente o validador required do campo CAR
  // conforme o tipo de arquivo
  private updateCarValidator(isCarRequired: boolean | undefined) {
    // Se o tipo de arquivo for Excel, mostrar o campo CAR.
    if (this.isExcelFile(this.fileName() ?? '')) {
      const carControl = this.form.controls.car;
    }
  }

  // Atualiza dinamicamente a visibilidade do campo CAR
  private updateCarFieldVisibility(showCarField: boolean | undefined) {
    this.isCarFieldHidden = !showCarField;

    if (this.isCarFieldHidden) {
      this.form.controls.car.setValue(null, { emitEvent: false });
    }
  }

  checkPk() {
    const fileName = this.fileName();
    const pKControl = this.form.controls.featurePrimaryKey;
    const primaryKeyFieldName = pKControl.value;

    // Limpa erros de duplicidade existentes primeiro
    if (pKControl.errors?.['duplicate']) {
      const { duplicate, ...otherErrors } = pKControl.errors;
      const hasOtherErrors = Object.keys(otherErrors).length > 0;
      pKControl.setErrors(hasOtherErrors ? otherErrors : null);
    }

    if (!fileName || !primaryKeyFieldName) {
      this.resetForm();

      return;
    }

    // Se for arquivo Excel, a validação já foi feita no getExcelMetadata
    // (e os campos são auto-preenchidos/desabilitados). Não chamar a
    // validação duplicada do endpoint validateInput.
    if (
      this.isKMLFile(fileName) ||
      this.isExcelFile(fileName) ||
      pKControl.disabled
    ) {
      return;
    }

    this.orpc.client.socEnvAnalysis
      .validateInput({ fileName, primaryKeyFieldName })
      .then(({ isValid }) => {
        if (!isValid) {
          pKControl.setErrors({
            ...pKControl.errors,
            duplicate: true,
          });
        }
      });
  }

  /**
   * Detecta o tipo de arquivo pela extensão
   */
  private isKMLFile(fileName: string): boolean {
    return fileName.endsWith('.kml') || fileName.endsWith('.kmz');
  }

  getMetadata() {
    const fileName = this.fileName();
    if (!fileName) return;

    // Detectar tipo de arquivo pela extensão
    if (this.isExcelFile(fileName)) {
      // Buscar metadados de Excel passando o módulo
      const module = this.module();
      this.orpc.client.storage
        .getExcelMetadata({ fileName, module })
        .then((meta) => {
          this.metadata =
            meta as unknown as backendOutput['storage']['getExcelMetadata'];
          // Preencher campos para Excel usando os headers retornados
          this.autoFillExcelFields(meta.fields);
        });
    } else if (this.isKMLFile(fileName)) {
      // Buscar metadados de KML/KMZ
      this.orpc.client.storage.getKMLMetadata({ fileName }).then((meta) => {
        this.metadata = meta;
        // Auto-preencher ID_AREA com nome original do arquivo (sem extensão)
        const originalName = this.originalFileName();
        this.autoFillKMLFields(originalName ?? fileName);
      });
    } else {
      // Buscar metadados de Shapefile (comportamento padrão)
      this.orpc.client.storage.getShpMetadata({ fileName }).then((meta) => {
        this.metadata = meta;
      });
    }
  }

  /**
   * Preenche automaticamente os campos do formulário com a conversão do Excel
   * Excel tem estrutura fixa, então mapeamento é automático
   */

  /**
   * Preenche automaticamente os campos do formulário com a conversão do Excel
   * Usa os headers retornados do backend (baseado no módulo configurado)
   * @param fields Headers das colunas retornadas pelo backend
   */
  private autoFillExcelFields(fields: string[]) {
    // Buscar os headers corretos retornados pelo backend
    // O backend já retorna os headers baseado no moduleConfig

    // Conversão baseada nos headers disponíveis:
    // Buscar ID_AREA → featurePrimaryKey
    // Buscar CONTRATO → contract
    // Buscar CAR → car
    // Buscar APOLICE → insurancePolicy
    // Buscar FAZENDA → farmName
    // Buscar PRODUTOR → producer
    // Buscar CPF_CNPJ → identityDocument

    const idAreaField = fields.find((f) => f === 'ID_AREA');
    const cpfCnpjField = fields.find((f) => f === 'CPF_CNPJ');
    const fazendaField = fields.find((f) => f === 'FAZENDA');

    const produtorField = fields.find((f) => f === 'PRODUTOR');
    const carField = fields.find((f) => f === 'CAR');
    const contratoField = fields.find((f) => f === 'CONTRATO');
    const apoliceField = fields.find((f) => f === 'APOLICE');

    this.form.patchValue({
      featurePrimaryKey: idAreaField || '',
      identityDocument: cpfCnpjField || '',
      producer: produtorField || '',
      car: carField || '',
      farmName: fazendaField || '',
      contract: contratoField || '',
      insurancePolicy: apoliceField || '',
    });

    // Desabilita os campos para que o usuário não possa alterar
    this.form.disable();
  }

  /**
   * Preenche automaticamente o campo ID_AREA para arquivos KML/KMZ
   * Usa o nome original do arquivo (sem extensão) como ID_AREA
   * e bloqueia todos os campos — não há colunas para mapear.
   * @param fileName Nome original do arquivo (ex.: "Areas_KMZ_1707.kmz")

   */
  private autoFillKMLFields(fileName: string) {
    // Extrair nome do arquivo sem extensão
    // Se for o nome original: "Fazenda.kmz" → "Fazenda"
    // Se for o hash: "sha256:abc123.kmz" → "abc123"
    let fileNameWithoutExtension = fileName
      .replace(/\.kmz$/i, '')
      .replace(/\.kml$/i, '');

    // Se ainda tiver o prefixo sha256:, remover
    if (fileNameWithoutExtension.startsWith('sha256:')) {
      fileNameWithoutExtension = fileNameWithoutExtension.replace(
        /^sha256:/,
        '',
      );
    }
    this.metadata?.fields.push(fileNameWithoutExtension);
    // Preencher apenas o campo ID_AREA com o nome do arquivo
    this.form.patchValue({
      featurePrimaryKey: fileNameWithoutExtension,
      identityDocument: null,
      producer: null,
      car: null,
      contract: null,
      insurancePolicy: null,
      farmName: null,
    });

    // Bloqueia tudo — KMZ não tem colunas para o usuário selecionar
    this.form.disable({ emitEvent: false });
  }

  resetForm() {
    this.form.reset({}, { emitEvent: false });
    // Habilita todos os campos novamente ao resetar
    this.form.enable({ emitEvent: false });
    this.metadata = null;
  }
}
